import axios from 'axios';
import MangaApiService from './MangaApiService';

const KODIK_API_BASE_URL = window.location.hostname === 'localhost'
    ? '/__kodik_api/api/v1'
    : 'https://3dpt.whitewhale.help/api/v1';

const kodikClient = axios.create({
    baseURL: KODIK_API_BASE_URL,
    timeout: 15000,
});

const toApiError = (error) => {
    const payload = error?.response?.data || {};
    const apiError = new Error(payload.message || error.message || 'Kodik API request failed');
    apiError.code = payload.code || 'KODIK_API_ERROR';
    apiError.status = error?.response?.status || 0;
    apiError.requestId = payload.requestId || null;
    apiError.retryAfter = error?.response?.headers?.['retry-after'] || null;
    apiError.details = payload.details || {};
    return apiError;
};

kodikClient.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(toApiError(error))
);

const compactParams = (params = {}) => Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
);

const getModeratorHeaders = async () => {
    const token = await MangaApiService.getJwt();
    if (!token) {
        const error = new Error('Для этой операции нужна авторизация');
        error.code = 'SESSION_REQUIRED';
        throw error;
    }
    return { Authorization: `Bearer ${token}` };
};

const KodikApiService = {
    getPlayer: async (animixAnimeId, options = {}) => {
        const response = await kodikClient.get(`/anime/${animixAnimeId}/player`, {
            params: compactParams({
                season: options.season,
                episode: options.episode,
                translationId: options.translationId,
                includeStreams: options.includeStreams ? true : undefined,
            }),
        });
        return response.data;
    },

    getEpisodes: async (animixAnimeId, options = {}) => {
        const response = await kodikClient.get(`/anime/${animixAnimeId}/episodes`, {
            params: compactParams({ translationId: options.translationId }),
        });
        return response.data;
    },

    getPlaybackSources: async (animixAnimeId, options = {}) => {
        try {
            const episodes = await KodikApiService.getEpisodes(animixAnimeId, options);
            return { ...episodes, moviePlayer: null };
        } catch (error) {
            if (error.status !== 409 || error.code !== 'NOT_A_SERIES') throw error;
            const moviePlayer = await KodikApiService.getPlayer(animixAnimeId, options);
            return {
                animixAnimeId: Number(animixAnimeId),
                kodikId: moviePlayer.kodikId,
                translation: moviePlayer.translation,
                items: [],
                moviePlayer,
                updatedAt: moviePlayer.updatedAt,
            };
        }
    },

    search: async (params = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get('/moderation/kodik/search', {
            headers,
            params: compactParams(params),
        });
        return response.data;
    },

    getCandidates: async (animixAnimeId, options = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get(`/moderation/anime/${animixAnimeId}/candidates`, {
            headers,
            params: options.refresh ? { refresh: true } : {},
        });
        return response.data;
    },

    getParts: async (animixAnimeId) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get(`/moderation/anime/${animixAnimeId}/parts`, { headers });
        return response.data;
    },

    createPartSegment: async (animixAnimeId, options = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.post(
            `/moderation/anime/${animixAnimeId}/parts`,
            compactParams({
                logicalSeason: options.logicalSeason,
                kodikId: options.kodikId,
                sourceSeason: options.sourceSeason,
                sourceEpisodeStart: options.sourceEpisodeStart,
                sourceEpisodeEnd: options.sourceEpisodeEnd,
                targetEpisodeStart: options.targetEpisodeStart,
                reason: options.reason,
            }),
            { headers }
        );
        return response.data;
    },

    updatePartSegment: async (animixAnimeId, partId, options = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.put(
            `/moderation/anime/${animixAnimeId}/parts/items/${partId}`,
            compactParams({
                logicalSeason: options.logicalSeason,
                kodikId: options.kodikId,
                sourceSeason: options.sourceSeason,
                sourceEpisodeStart: options.sourceEpisodeStart,
                sourceEpisodeEnd: options.sourceEpisodeEnd,
                targetEpisodeStart: options.targetEpisodeStart,
                reason: options.reason,
            }),
            { headers }
        );
        return response.data;
    },

    deletePartSegment: async (animixAnimeId, partId) => {
        const headers = await getModeratorHeaders();
        await kodikClient.delete(`/moderation/anime/${animixAnimeId}/parts/items/${partId}`, { headers });
    },

    getAnimeIntegrations: async (options = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get('/moderation/anime', {
            headers,
            params: compactParams({
                page: options.page ?? 0,
                size: options.size ?? 50,
                query: options.query,
                status: Array.isArray(options.status) ? options.status.join(',') : options.status,
                sort: options.sort,
                direction: options.direction,
            }),
        });
        return response.data;
    },

    setPart: async (animixAnimeId, logicalSeason, options = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.put(
            `/moderation/anime/${animixAnimeId}/parts/${logicalSeason}`,
            {
                kodikId: options.kodikId,
                ...(options.sourceSeason ? { sourceSeason: options.sourceSeason } : {}),
                reason: options.reason || '',
            },
            { headers }
        );
        return response.data;
    },

    deletePart: async (animixAnimeId, logicalSeason) => {
        const headers = await getModeratorHeaders();
        await kodikClient.delete(`/moderation/anime/${animixAnimeId}/parts/${logicalSeason}`, { headers });
    },

    getConflicts: async (page = 0, size = 50) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get('/moderation/conflicts', {
            headers,
            params: { page, size },
        });
        return response.data;
    },

    setShikimoriMatch: async (animixAnimeId, shikimoriId, options = {}) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.put(
            `/moderation/anime/${animixAnimeId}/shikimori`,
            {
                shikimoriId: String(shikimoriId),
                ...(options.kodikId ? { kodikId: options.kodikId } : {}),
                reason: options.reason || '',
            },
            { headers }
        );
        return response.data;
    },

    setMatch: async (animixAnimeId, kodikId, reason) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.put(
            `/moderation/anime/${animixAnimeId}/match`,
            { kodikId, reason: reason || '' },
            { headers }
        );
        return response.data;
    },

    deleteMatch: async (animixAnimeId, kodikId) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.delete(
            `/moderation/anime/${animixAnimeId}/match/${encodeURIComponent(kodikId)}`,
            { headers }
        );
        return response.data;
    },

    startRematch: async (animixAnimeId) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.post(`/moderation/anime/${animixAnimeId}/rematch`, null, { headers });
        return response.data;
    },

    getRematchStatus: async (taskId) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get(`/moderation/rematch/${taskId}`, { headers });
        return response.data;
    },

    startSync: async (mode = 'INCREMENTAL') => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.post('/moderation/sync', { mode }, { headers });
        return response.data;
    },

    getSyncStatus: async (runId) => {
        const headers = await getModeratorHeaders();
        const response = await kodikClient.get(`/moderation/sync/${runId}`, { headers });
        return response.data;
    },
};

export default KodikApiService;
